"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { UserRoundCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const navItems = [
  { label: "首页", href: "/", match: (path: string) => path === "/" },
  { label: "AI遗产查询", href: "/reports/new", match: (path: string) => path === "/reports/new" },
  {
    label: "我的报告",
    href: "/reports",
    match: (path: string) =>
      path === "/reports" || (path.startsWith("/reports/") && path !== "/reports/new"),
  },
  {
    label: "遗产知识库",
    href: "/knowledge",
    match: (path: string) => path === "/knowledge" || path.startsWith("/knowledge/"),
  },
  { label: "遗产问答", href: "/qa", match: (path: string) => path === "/qa" || path.startsWith("/qa/") },
];

type MeResponse = {
  ok: boolean;
  data?: {
    user?: {
      email: string;
      nickname?: string | null;
    };
  };
};

export function SiteHeader() {
  const pathname = usePathname();
  const [userLabel, setUserLabel] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadUser() {
      try {
        const response = await fetch("/api/auth/me", { cache: "no-store" });
        if (!response.ok) {
          if (isMounted) setUserLabel(null);
          return;
        }
        const result = (await response.json()) as MeResponse;
        const user = result.data?.user;
        if (isMounted && user) {
          setUserLabel(user.email);
        }
      } catch {
        // Anonymous users keep the login button.
      }
    }

    void loadUser();

    return () => {
      isMounted = false;
    };
  }, [pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-[#e8e8e8] bg-white/95 backdrop-blur-sm">
      <div className="container flex min-h-20 flex-col gap-4 py-3 lg:flex-row lg:items-center lg:justify-between">
        <Link href="/" className="flex items-center gap-3">
          <span className="flex size-14 shrink-0 overflow-hidden rounded-md bg-white">
            <Image
              src="/brand/logo.png"
              alt="遗产指南针 logo"
              width={56}
              height={56}
              className="size-full object-cover"
              priority
            />
          </span>
          <span>
            <span className="block text-2xl font-bold tracking-normal text-[#1a1a1a]">
              遗产指南针
            </span>
            <span className="block text-sm font-medium text-primary">www.ycznz.com</span>
          </span>
        </Link>

        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <nav className="flex flex-wrap items-center gap-1 text-sm">
            {navItems.map((item) => {
              const isActive = item.match(pathname);
              return (
                <Button
                  key={item.href}
                  asChild
                  variant="ghost"
                  className={cn(
                    "px-3 text-[#1a1a1a]",
                    isActive && "bg-primary text-[#1a1a1a] hover:bg-[#b8995a] hover:text-[#1a1a1a]",
                  )}
                >
                  <Link href={item.href}>{item.label}</Link>
                </Button>
              );
            })}
          </nav>
          <Button
            asChild
            variant="outline"
            className="max-w-[220px] border-primary/50 text-[#1a1a1a] hover:bg-accent"
          >
            <Link href={userLabel ? "/reports" : "/login"} className="truncate">
              {userLabel ? (
                <>
                  <UserRoundCheck className="mr-2 size-4 shrink-0" />
                  <span className="truncate">已登录：{userLabel}</span>
                </>
              ) : (
                "登录"
              )}
            </Link>
          </Button>
        </div>
      </div>
    </header>
  );
}
